import React from "react";
import { Card, CardContent } from "../ui/card";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";

interface HintBannerProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  onActivate: () => void;
  className?: string;
}

export const HintBanner = React.forwardRef<HTMLDivElement, HintBannerProps>(
  ({ title, description, icon, onActivate, className }, ref) => {
    const titleStyle = {
      color: "color-mix(in srgb, var(--foreground) 77%, var(--surface))",
    } as const;
    const iconStyle = {
      color:
        "color-mix(in srgb, color-mix(in srgb, var(--primary) 70%, var(--accent)) 90%, var(--foreground))",
    } as const;
    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        onActivate();
      }
    };

    return (
      <Card
        ref={ref}
        role="button"
        tabIndex={0}
        onClick={onActivate}
        onKeyDown={handleKeyDown}
        className={`w-full cursor-pointer border border-blue-200 bg-blue-50/90 transition hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:border-blue-900/50 dark:bg-blue-900/30 ${
          className ?? ""
        }`}
      >
        <CardContent className="py-1.5 px-3 flex items-center justify-between gap-3 [&:last-child]:pb-2">
          <div className="flex items-center gap-3">
            <div className="rounded-full bg-blue-100 p-1.5 text-blue-700 dark:bg-blue-900/60 dark:text-blue-200">
              {icon}
            </div>
            <p className="font-medium" style={titleStyle}>
              {title}
            </p>
          </div>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                aria-label={description}
                className="flex h-6 w-6 items-center justify-center rounded-full bg-slate-100 text-xs font-semibold text-slate-700 transition hover:bg-slate-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
                style={iconStyle}
              >
                ?
              </button>
            </TooltipTrigger>
            <TooltipContent
              side="bottom"
              align="center"
              sideOffset={6}
              className="max-w-[min(90vw,20rem)] whitespace-normal text-left leading-relaxed break-words"
            >
              {description}
            </TooltipContent>
          </Tooltip>
        </CardContent>
      </Card>
    );
  }
);

HintBanner.displayName = "HintBanner";
